import { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Images from "../../utils/Images";
import { heroSlides } from "../../data/heroSlides";
import HeroBanner from "../insurance/HeroBanner";
import PrimaryFilters from "../insurance/PrimaryFilters";
import SecondaryFilters from "../insurance/SecondaryFilters";
import InsuranceGrid from "../insurance/InsuranceGrid";
import useInsuranceData from "../insurance/useInsuranceData";
import Footer from "./Footer";
import PackageDrawer from "../insurance/PackageDrawer";
import AdvancedSearchDrawer from "../insurance/AdvancedSearchDrawer";
import policyService from "../../api/PolicyService";
import { toast } from "react-toastify";
import Loader from "../misc/Loader";

// Mapping of filters to a specific hero slide index
const filterConfigs = [
  {
    key: "daily",
    title: "Daily Commuters",
    image: Images.daily_commuter,
    slideIndex: 0,
    isActive: true,
  },
  {
    key: "intercity",
    title: "Intercity Travel",
    image: Images.intercity_travel,
    slideIndex: 1,
    isActive:true
  },
  {
    key: "bikers",
    title: "Motor Riders",
    image: Images.okada,
    slideIndex: 2,
    isActive:true
  },
  // {
  //   key: "drivers",
  //   title: "Trotro, Taxi, Bolt Drivers",
  //   image: Images.taxi,
  //   slideIndex: 3,
  // },
];

// Map filter keys to API policy types
const getPolicyTypeFromKey = (key) => {
  const keyMap = {
    daily: "daily_commuter",
    intercity: "intercity_travel",
    bikers: "okada_rider",
  };
  return keyMap[key] || "intercity_travel";
};

export default function InsuranceHero() {
  const [selectedKey, setSelectedKey] = useState(filterConfigs[1].key);
  const [secondaryTab, setSecondaryTab] = useState("featured");
  const [currentPage, setCurrentPage] = useState(1);
  const [isPackageOpen, setIsPackageOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const isInitialMount = useRef(true);

  // Fetch policies whenever selectedKey changes
  useEffect(() => {
    const fetchPolicies = async () => {
      // Use loading state for initial mount, filterLoading for subsequent changes
      if (isInitialMount.current) {
        setLoading(true);
      } else {
        setFilterLoading(true);
      }

      try {
        const policyType = getPolicyTypeFromKey(selectedKey);
        const response = await policyService.getPolicies(policyType);
        if (response.data?.status_code === 200) {
          setPolicies(response.data.data || []);
        } else {
          toast.error(
            response.data?.reason || "An error occurred while fetching policies"
          );
          setPolicies([]);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.reason ||
            "An error occurred while fetching policies"
        );
        setPolicies([]);
      } finally {
        if (isInitialMount.current) {
          setLoading(false);
          isInitialMount.current = false;
        } else {
          setFilterLoading(false);
        }
      }
    };
    fetchPolicies();
  }, [selectedKey]);

  const currentSlideIndex = useMemo(() => {
    const found = filterConfigs.find((f) => f.key === selectedKey && f.isActive);
    return found ? found.slideIndex : 0;
  }, [selectedKey]);

  const currentSlide = heroSlides[currentSlideIndex];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  //* Reset page when filters change
  const resetPageOnFilterChange = (key, tab) => {
    setSelectedKey(key);
    if (typeof tab === "string") setSecondaryTab(tab);
    setCurrentPage(1);
    // filterLoading will be set by the useEffect when selectedKey changes
  };

  //* Use modular data hook with real policy data and advanced filters
  const allItems = useInsuranceData(
    selectedKey,
    secondaryTab,
    policies,
    advancedFilters
  );

  const ITEMS_PER_PAGE = 8;
  const totalPages = Math.max(1, Math.ceil(allItems.length / ITEMS_PER_PAGE));
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return allItems.slice(start, start + ITEMS_PER_PAGE);
  }, [allItems, currentPage]);

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  if (loading) {
    return <Loader fullScreen={true} text="Loading policies..." />;
  }

  return (
    <section className="overflow-hidden relative">
      {/* Hero */}
      <div className="overflow-hidden relative z-0 pt-14 w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlideIndex}
            className=""
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <HeroBanner slide={currentSlide} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Filters */}
      <div className="relative z-10 px-4 py-6 -mt-1 bg-gradient-to-b from-white to-gray-50 sm:py-8 md:py-10 lg:py-12 sm:-mt-2 md:-mt-3 lg:-mt-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex justify-between items-end mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
                Select Your Cover
              </h3>
              <div className="mt-2 w-28 h-1 bg-emerald-500 rounded" />
            </div>
            <p className="hidden text-sm text-gray-600 sm:block">
              Click a filter to preview the matching protection
            </p>
          </div>

          <PrimaryFilters
            filters={filterConfigs}
            selectedKey={selectedKey}
            onSelect={(key) => resetPageOnFilterChange(key)}
            loading={filterLoading}
          />

          {/* Secondary Filters */}
          <div className="mt-8">
            <SecondaryFilters
              tab={secondaryTab}
              onTabChange={(key) => resetPageOnFilterChange(selectedKey, key)}
              disableOpen={isPackageOpen}
              open={isAdvancedOpen}
              onOpenChange={setIsAdvancedOpen}
            />

            {/* Advanced Search Drawer */}
            <AdvancedSearchDrawer
              open={isAdvancedOpen}
              onClose={() => setIsAdvancedOpen(false)}
              onApply={(filters) => {
                setAdvancedFilters(filters);
                setCurrentPage(1);
              }}
            />

            {/* Results grid */}
            {loading || filterLoading ? (
              <div className="flex justify-center items-center py-12 mt-6">
                <div className="flex flex-col gap-4 items-center">
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full border-b-2 border-emerald-500 animate-spin"></div>
                    <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-emerald-200 animate-pulse"></div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {filterLoading
                      ? "Loading fresh policies..."
                      : "Loading policies..."}
                  </p>
                  {filterLoading && (
                    <p className="text-xs text-gray-500">
                      Finding the best options for you
                    </p>
                  )}
                </div>
              </div>
            ) : allItems.length === 0 ? (
              <div className="flex justify-center items-center py-12 mt-6">
                <div className="flex flex-col gap-4 items-center text-center">
                  <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      No policies found
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      No policies available for the selected category. Try
                      selecting a different filter.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div
                onClickCapture={(e) => {
                  const card = e.target.closest("[data-card-item]");
                  if (card) {
                    const id = card.getAttribute("data-id");
                    const found = pageItems.find((i) => i.id === id);
                    setSelectedItem(found || null);
                    setIsPackageOpen(true);
                    // ensure advanced drawer is closed
                    setIsAdvancedOpen(false);
                  }
                }}
              >
                <InsuranceGrid
                  items={pageItems}
                  currentPage={currentPage}
                  total={allItems.length}
                  perPage={ITEMS_PER_PAGE}
                  onPrev={handlePrev}
                  onNext={handleNext}
                  onGo={(n) => setCurrentPage(n)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <PackageDrawer
        open={isPackageOpen}
        onClose={() => setIsPackageOpen(false)}
        item={selectedItem}
      />
    </section>
  );
}
